<script lang="ts">
  import { building } from "$app/env";
  import { page } from "$app/state";
  import { m } from "#messages";
  import { publicHref } from "#lib/locale.ts";
  import { loginHref } from "#lib/entrance/login.ts";
  import { PRICING_PATH } from "#lib/routes.ts";
  import type AccountPanel from "#lib/shell/AccountPanel.svelte";
  import type ProductsPanel from "./ProductsPanel.svelte";
  import type ResourcesPanel from "./ResourcesPanel.svelte";
  import AvatarMenu from "#lib/shell/AvatarMenu.svelte";
  import type { SessionUser } from "#lib/shell/me.ts";
  import Button from "#lib/ui/Button.svelte";
  import Icon from "#lib/ui/Icon.svelte";
  import { FOCUS_RING, MENU_SURFACE, TAP_TARGET } from "#lib/ui/recipes.ts";
  import IconDiscord from "~icons/simple-icons/discord";
  import HeaderBrand from "./HeaderBrand.svelte";
  import MobileMenu from "./MobileMenu.svelte";
  import { DEMO } from "./products.ts";

  let { me = null, minimal = false }: { me?: SessionUser | null; minimal?: boolean } = $props();

  type MenuKey = "products" | "resources";

  const SCROLLED_PAST = 8;

  let leaving = $state(false);
  const href = $derived(loginHref(page.url, building));

  let sheet = $state.raw<{ AccountPanel: typeof AccountPanel } | null>(null);
  let preparing: Promise<void> | undefined;

  const prepareSheet = (): Promise<void> =>
    (preparing ??= import("#lib/shell/AccountPanel.svelte").then(
      (account) => {
        sheet = { AccountPanel: account.default };
      },
      () => {
        preparing = undefined;
      },
    ));

  let open = $state<MenuKey | null>(null);
  let scrolled = $state(false);
  $effect(() => {
    scrolled = scrollY > SCROLLED_PAST;
    const sentinel = document.createElement("div");
    sentinel.ariaHidden = "true";
    sentinel.style.cssText = `position:absolute;top:0;left:0;width:1px;height:${SCROLLED_PAST}px;visibility:hidden;pointer-events:none`;
    document.body.prepend(sentinel);
    const observer = new IntersectionObserver((entries) => {
      const latest = entries.at(-1);
      if (latest !== undefined) scrolled = !latest.isIntersecting;
    });
    observer.observe(sentinel);
    return () => {
      observer.disconnect();
      sentinel.remove();
    };
  });
  let hovering: MenuKey | null = null;
  let closeTimer = 0;
  let nav = $state<HTMLElement | null>(null);

  let panels = $state.raw<{
    products: typeof ProductsPanel;
    resources: typeof ResourcesPanel;
  } | null>(null);
  let loadingPanels: Promise<void> | undefined;

  const preparePanels = (): Promise<void> =>
    (loadingPanels ??= Promise.all([
      import("./ProductsPanel.svelte"),
      import("./ResourcesPanel.svelte"),
    ]).then(
      ([products, resources]) => {
        panels = { products: products.default, resources: resources.default };
      },
      () => {
        loadingPanels = undefined;
      },
    ));

  $effect(() => {
    if (minimal) return;
    const timer = setTimeout(() => void preparePanels(), 0);
    return () => clearTimeout(timer);
  });

  const show = (key: MenuKey) => (event: PointerEvent) => {
    if (event.pointerType !== "mouse") return;
    void preparePanels();
    hovering = key;
    clearTimeout(closeTimer);
    open = key;
  };

  const hideSoon = (event: PointerEvent): void => {
    if (event.pointerType !== "mouse") return;
    hovering = null;
    clearTimeout(closeTimer);
    closeTimer = window.setTimeout(() => (open = null), 160);
  };

  const toggle = (key: MenuKey): void => {
    void preparePanels();
    open = hovering === key || open !== key ? key : null;
  };

  const onkeydown = (event: KeyboardEvent): void => {
    if (open !== null && event.key === "Escape") open = null;
  };

  const onpointerdown = (event: PointerEvent): void => {
    if (open !== null && event.target instanceof Node && nav?.contains(event.target) !== true) {
      open = null;
    }
  };

  const close = (): void => {
    open = null;
  };

  const LINK =
    "flex items-center rounded-control text-lg font-medium tracking-tight text-ink-2 transition-colors duration-120 hover:text-ink-1 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-focus " +
    TAP_TARGET;
</script>

<svelte:window {onkeydown} {onpointerdown} />

{#snippet brand(onclick?: () => void)}
  <a
    href={publicHref("/")}
    aria-label={m.app_name()}
    {onclick}
    class="-mx-2 flex h-9 items-center rounded-control px-2 text-ink-1 {FOCUS_RING}"
  >
    <HeaderBrand compact={scrolled} />
  </a>
{/snippet}

{#snippet signIn(size: "md" | "lg")}
  {#if me !== null && size === "lg"}
    {#if sheet !== null}<sheet.AccountPanel {me} />{/if}
  {:else if me !== null}
    <AvatarMenu {me} />
  {:else if size === "md"}
    <span class="flex items-center gap-2">
      <Button
        variant="subtle"
        size="md"
        shape="round"
        href={DEMO.href}
        data-sveltekit-reload
        data-testid="header-demo"
      >
        {m.marketing_menu_demo_short()}
      </Button>
      <Button
        variant="primary"
        size="md"
        shape="round"
        {href}
        loading={leaving}
        data-sveltekit-reload
        data-testid="login-discord"
        onclick={() => (leaving = true)}
      >
        {m.marketing_signin()}
      </Button>
    </span>
  {:else}
    <div class="flex flex-col gap-3">
      <Button
        variant="subtle"
        size="lg"
        shape="round"
        href={DEMO.href}
        data-sveltekit-reload
        data-testid="mobile-demo"
      >
        {DEMO.label()}
      </Button>
      <Button
        variant="primary"
        size="lg"
        shape="round"
        {href}
        loading={leaving}
        data-sveltekit-reload
        onclick={() => (leaving = true)}
      >
        <IconDiscord aria-hidden="true" class="size-5 shrink-0" />
        {m.login_cta()}
      </Button>
    </div>
  {/if}
{/snippet}

{#snippet trigger(key: MenuKey, label: string)}
  <div role="presentation" onpointerenter={show(key)} onpointerleave={hideSoon}>
    <button
      type="button"
      aria-expanded={open === key}
      aria-controls="{key}-panel"
      data-testid="header-{key}"
      onclick={() => toggle(key)}
      class={["flex items-center gap-1", LINK, open === key && "text-ink-1"]}
    >
      {label}
      <span class={["flex transition-transform duration-200", open === key && "rotate-180"]}>
        <Icon name="caretDown" size={10} />
      </span>
    </button>
    {#if panels !== null}
      <div
        id="{key}-panel"
        data-testid="{key}-panel"
        inert={open !== key}
        class={[
          MENU_SURFACE,
          "panel absolute top-full left-1/2 mt-7 -translate-x-1/2 p-2",
          open === key && "shown",
        ]}
      >
        {#if key === "products"}
          <panels.products onpick={close} />
        {:else}
          <panels.resources onpick={close} />
        {/if}
      </div>
    {/if}
  </div>
{/snippet}

<header class="sticky top-0 z-40 h-20 pt-3" data-testid="entrance-header">
  <div class={["shell mx-auto", scrolled && "lifted"]}>
    <div
      class="flex h-14 items-center justify-between gap-6 md:grid md:grid-cols-[1fr_auto_1fr]"
      data-entrance-bar
    >
      <div class="justify-self-start">{@render brand()}</div>

      {#if minimal}
        <div class="flex items-center justify-self-end md:col-start-3">{@render signIn("md")}</div>
      {:else}
        <nav bind:this={nav} class="relative flex items-center gap-8 max-md:hidden">
          {@render trigger("products", m.marketing_products())}
          {@render trigger("resources", m.marketing_resources())}
          <a href={publicHref(PRICING_PATH)} data-testid="header-pricing" class={LINK}
            >{m.pricing_link()}</a
          >
        </nav>

        <div class="flex items-center justify-self-end">
          <div class="max-md:hidden">{@render signIn("md")}</div>
          <div class="md:hidden">
            <MobileMenu {brand} prepare={prepareSheet}>
              {#snippet account()}
                {@render signIn("lg")}
              {/snippet}
            </MobileMenu>
          </div>
        </div>
      {/if}
    </div>
  </div>
</header>

<style>
  .shell {
    max-width: 87.5rem;
    padding-inline: clamp(1.25rem, 4vw, 3rem);
    border: 1px solid transparent;
    border-radius: 1rem;
    transition:
      max-width 400ms cubic-bezier(0.2, 0.7, 0.2, 1),
      padding 400ms cubic-bezier(0.2, 0.7, 0.2, 1),
      background-color 300ms ease-out,
      border-color 300ms ease-out,
      box-shadow 300ms ease-out;
  }

  .lifted {
    max-width: min(64rem, calc(100% - 1.5rem));
    padding-inline: 1.25rem 0.625rem;
    background: color-mix(in srgb, var(--surface-0) 82%, transparent);
    border-color: var(--edge);
    box-shadow: var(--menu-shadow);
    backdrop-filter: blur(14px) saturate(1.4);
  }

  @media (prefers-reduced-motion: reduce) {
    .shell {
      transition: none;
    }
  }

  .panel {
    visibility: hidden;
    opacity: 0;
    scale: 0.98;
    transform-origin: top center;
    transition:
      opacity 160ms ease-out,
      scale 160ms ease-out,
      visibility 0s 160ms;
  }

  .panel.shown {
    visibility: visible;
    opacity: 1;
    scale: 1;
    transition:
      opacity 180ms cubic-bezier(0.2, 0.7, 0.2, 1),
      scale 220ms cubic-bezier(0.2, 0.7, 0.2, 1),
      visibility 0s;
  }

  .panel::before {
    content: "";
    position: absolute;
    inset: -1.75rem 0 100%;
  }

  @media (prefers-reduced-motion: reduce) {
    .panel,
    .panel.shown {
      transition: none;
    }
  }
</style>
